import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AssetUrls } from 'config/assets';

interface CardImageTooltipProps {
  scryfallId?: string;
  imageUrl?: string;
  normalFallbacks?: string[];
  cardName: string;
  position: { x: number; y: number };
  isLoading?: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const TOOLTIP_WIDTH = 240;
const TOOLTIP_HEIGHT = 335; // Standard MTG card aspect ratio ~1:1.4
const TOOLTIP_PADDING = 16; // 8px padding on each side
const VIEWPORT_MARGIN = 10;

const CardImageTooltip = ({
  scryfallId,
  imageUrl,
  normalFallbacks,
  cardName,
  position,
  isLoading,
  onMouseEnter,
  onMouseLeave,
}: CardImageTooltipProps) => {
  const [imgError, setImgError] = useState(false);
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);
  const fallbackIndexRef = useRef(0);

  // Calculate smart position to avoid viewport edges
  const calculatePosition = () => {
    const tooltipFullWidth = TOOLTIP_WIDTH + TOOLTIP_PADDING;
    const tooltipFullHeight = TOOLTIP_HEIGHT + TOOLTIP_PADDING;

    let x = position.x + 15; // Offset from cursor
    let y = position.y + 15;

    // Prevent overflow right
    if (x + tooltipFullWidth > window.innerWidth - VIEWPORT_MARGIN) {
      x = position.x - tooltipFullWidth - 15;
    }

    // Prevent overflow bottom
    if (y + tooltipFullHeight > window.innerHeight - VIEWPORT_MARGIN) {
      y = position.y - tooltipFullHeight - 15;
    }

    // Prevent overflow left
    if (x < VIEWPORT_MARGIN) {
      x = VIEWPORT_MARGIN;
    }

    // Prevent overflow top
    if (y < VIEWPORT_MARGIN) {
      y = VIEWPORT_MARGIN;
    }

    return { x, y };
  };

  // Initialize image source
  useEffect(() => {
    fallbackIndexRef.current = 0;
    setImgError(false);

    if (imageUrl) {
      setCurrentSrc(imageUrl);
    } else if (scryfallId) {
      setCurrentSrc(AssetUrls.cards.getCardImage(scryfallId, 'normal'));
    } else {
      setCurrentSrc(null);
    }
  }, [scryfallId, imageUrl]);

  // Handle image load error with fallback cascade
  const handleImageError = () => {
    // Try backend-provided fallbacks first
    if (normalFallbacks && fallbackIndexRef.current < normalFallbacks.length) {
      setCurrentSrc(normalFallbacks[fallbackIndexRef.current]);
      fallbackIndexRef.current++;
      return;
    }

    // Then try CDN size variations
    if (scryfallId) {
      const currentSrcValue = currentSrc || '';

      if (currentSrcValue.includes('/normal/')) {
        setCurrentSrc(AssetUrls.cards.getCardImage(scryfallId, 'small'));
        return;
      }
      if (currentSrcValue.includes('/small/')) {
        setCurrentSrc(AssetUrls.cards.getCardImage(scryfallId, 'large'));
        return;
      }
    }

    // All fallbacks exhausted
    setImgError(true);
  };

  const pos = calculatePosition();

  const tooltipContent = (
    <div
      className="card-image-tooltip"
      style={{
        left: `${pos.x}px`,
        top: `${pos.y}px`,
      }}
      role="tooltip"
      aria-label={`${cardName} card image`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isLoading ? (
        <div className="card-image-tooltip-loading" aria-label="Loading card image" />
      ) : imgError || !currentSrc ? (
        <div className="card-image-tooltip-error">
          <span className="card-image-tooltip-error-icon">&#x1F0CF;</span>
          <span>Image unavailable</span>
        </div>
      ) : (
        <img
          src={currentSrc}
          alt={`${cardName} - Magic: The Gathering card`}
          onError={handleImageError}
          loading="eager"
        />
      )}
    </div>
  );

  return createPortal(tooltipContent, document.body);
};

export default CardImageTooltip;
